// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  CloudDoneOutlined,
  CloudUploadOutlined,
  HourglassEmptyOutlined,
  Upload,
} from '@/constants/hardfin-standard-icons';
import { formatDuration } from '@/utilities/format-duration';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  DialogActions,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const MOVE_SEC = 0.025;
const MAX_UPLOAD_SECONDS = 600; // 10 minutes

interface CsvUploadProps {
  uploadErrorMessage: string | null;
  file: File | null;
  isUploading: boolean;
  onSelectFile: () => void;
  onUpload: () => void;
  onBack: () => void;
  onClose?: () => void;
  hasFatalError?: boolean;
  uploadLineCount?: number;
}

export function CsvUpload(props: CsvUploadProps) {
  const estimatedUploadTime = props.uploadLineCount
    ? Math.round(2 + props.uploadLineCount * MOVE_SEC)
    : 0;

  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUpload = () => {
    props.onUpload();
    setCountdown(estimatedUploadTime);
  };

  useEffect(() => {
    if (props.isUploading && countdown > 0 && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    }

    if (!props.isUploading) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCountdown(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [props.isUploading, countdown]);

  return (
    <Card>
      <CardContent>
        {props.uploadErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {props.uploadErrorMessage}
          </Alert>
        )}

        {props.hasFatalError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Upload failed</AlertTitle>
            Something went wrong with your upload, support has been notified.
          </Alert>
        ) : (
          <>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                mb: 3,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'grey.50',
                },
              }}
              onClick={props.onSelectFile}
            >
              {props.file ? (
                <CloudDoneOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              ) : (
                <CloudUploadOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              )}
              <Typography variant="h6" gutterBottom>
                {props.file
                  ? props.file.name
                  : 'Upload a CSV file to import moves'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {props.file
                  ? `Ready to import ${formatNumber(props.uploadLineCount ?? 0)} moves (${formatNumber(Math.round(props.file.size / 1024))} KB)`
                  : 'Click to browse for your CSV file'}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>About move imports</AlertTitle>
              <ul
                style={{
                  paddingLeft: '1.5rem',
                  marginTop: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <li>A move will be created for each row in your CSV</li>
                <li>Assets must already exist (looked up by serial)</li>
                <li>Locations must already exist (looked up by name)</li>
              </ul>
            </Alert>
          </>
        )}

        {estimatedUploadTime >= MAX_UPLOAD_SECONDS && !props.isUploading && (
          <Alert severity="error" sx={{ mb: 2, width: '50%', mx: 'auto' }}>
            <AlertTitle>File is too large to upload</AlertTitle>
            This file will take too long to process. Please break it into
            smaller files and upload them separately
          </Alert>
        )}
        {estimatedUploadTime >= 60 &&
          estimatedUploadTime < MAX_UPLOAD_SECONDS &&
          !props.isUploading && (
            <Alert severity="warning" sx={{ mb: 2, width: '50%', mx: 'auto' }}>
              <AlertTitle>This data looks large</AlertTitle>
              Consider breaking the file into smaller pieces for reliability
            </Alert>
          )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignContent={'center'}
        >
          <DialogActions>
            <Button
              variant="outlined"
              onClick={props.onBack}
              disabled={props.isUploading}
            >
              Back
            </Button>
          </DialogActions>
          <Box display="flex" alignItems={'center'}>
            {props.uploadLineCount && !props.isUploading && (
              <Typography color={'text.secondary'}>
                Estimated processing time: {formatDuration(estimatedUploadTime)}
              </Typography>
            )}
            {props.isUploading && countdown > 0 && (
              <Box display="flex" alignItems="center" gap={1}>
                <HourglassEmptyOutlined
                  sx={{
                    color: 'text.secondary',
                    fontSize: 20,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        opacity: 0.4,
                      },
                      '50%': {
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Typography color={'text.secondary'}>
                  Estimated time remaining: {formatDuration(countdown)}
                </Typography>
              </Box>
            )}
            {props.isUploading && countdown === 0 && (
              <Typography color={'text.secondary'}>
                Almost finished...
              </Typography>
            )}
          </Box>
          <DialogActions>
            {props.hasFatalError && props.onClose ? (
              <Button variant="contained" onClick={props.onClose}>
                Close
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={
                  !props.file ||
                  props.isUploading ||
                  estimatedUploadTime >= MAX_UPLOAD_SECONDS
                }
                startIcon={
                  props.isUploading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Upload />
                  )
                }
              >
                {props.isUploading ? 'Importing moves...' : 'Import moves'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </CardContent>
    </Card>
  );
}
